
import React, { useEffect, useRef, useState } from "react";
import { useScroll, useTransform, motion, useSpring } from "framer-motion";
import { cn } from "@/lib/utils";
import { trackWhatsAppClick } from "@/lib/gtm";

export interface TimelineEntry {
  title: string;
  description: string;
  benefit: string;
  image: string;
  buttonLabel: string;
  buttonHref: string;
}

interface TimelineProps {
  data: TimelineEntry[];
  title: string;
  subtitle: string;
}

export const Timeline = ({ data, title, subtitle }: TimelineProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (ref.current) {
          setHeight(ref.current.getBoundingClientRect().height);
        }
      });
      resizeObserver.observe(ref.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 95%"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 70,
    damping: 30,
    restDelta: 0.001,
  });

  const heightTransform = useTransform(smoothProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(smoothProgress, [0, 0.1], [0, 1]);

  return (
    <section
      className="w-full bg-white selection:bg-primary/10 pb-12 md:pb-20 overflow-hidden"
      ref={containerRef}
      id="produtos"
    >
      <div className="container mx-auto pt-12 md:pt-32 pb-10 md:pb-20 px-6 text-center md:text-left">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-2xl md:text-3xl lg:text-5xl font-extrabold text-foreground mb-3 md:mb-6"
        >
          {title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-gray-500 font-normal text-lg lg:text-xl max-w-2xl mx-auto md:mx-0 leading-snug md:leading-relaxed"
        >
          {subtitle}
        </motion.p>
      </div>

      <div ref={ref} className="relative container mx-auto pb-12 md:pb-40 px-6">
        {data.map((item, index) => (
          <div
            key={index}
            className="relative px-0 md:px-6 md:pl-24 pt-4 md:pt-40 mb-20 md:mb-32 last:mb-0"
          >
            {/* Timeline dot indicator */}
            <div className="absolute left-0 md:left-3 top-20 md:top-40 z-40 hidden md:block">
              <div className="h-6 w-6 rounded-full bg-primary border-4 border-white shadow-2xl" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
              className="grid md:grid-cols-[45%_55%] gap-4 md:gap-12 lg:gap-24 items-center"
            >
              {/* Product Content - Moved after image on mobile */}
              <div className="flex flex-col items-center md:items-start text-center md:text-left order-2 md:order-1 px-6 md:px-0">
                <h3 className="text-2xl lg:text-4xl font-extrabold text-primary mb-2 md:mb-6 leading-tight tracking-tight">
                  {item.title}
                </h3>
                <p className="text-gray-600 font-normal text-lg lg:text-xl mb-6 md:mb-10 leading-snug md:leading-relaxed max-w-md mx-auto md:mx-0">
                  {item.description}
                </p>

                <a
                  href={item.buttonHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick(item.buttonLabel, item.buttonHref, "produtos")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="produtos"
                  className="inline-flex items-center justify-center bg-primary text-white px-10 py-4 md:py-5 rounded-2xl font-bold text-lg hover:bg-secondary transition-all transform hover:-translate-y-1 shadow-2xl shadow-primary/20"
                >
                  {item.buttonLabel}
                </a>
              </div>

              {/* Product Image - Moved before text on mobile */}
              <div className="order-1 md:order-2 flex justify-center">
                <div className="w-full transition-transform duration-700 hover:scale-[1.02] flex items-center justify-center">
                  <img
                    src={item.image}
                    alt={item.title}
                    width="800"
                    height="600"
                    loading="lazy"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        ))}

        {/* Timeline Line */}
        <div
          style={{ height: height + "px" }}
          className="absolute left-6 md:left-9 top-0 overflow-hidden w-[4px] bg-gray-100 hidden md:block"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[4px] bg-primary rounded-full"
          />
        </div>
      </div>
    </section>
  );
};
